import { useMemo, useState, useCallback } from 'react';
import type { IPaginationState } from 'src/utils/server-side-pagination';
import { applySorting, dispatchSorting } from 'src/utils/server-side-pagination';

export interface UseServerTableStateProps {
  initialFilters: Record<string, any>;
  initialPage?: number;
  initialSorting?: Record<string, 'asc' | 'desc'>;
}

export interface UseServerTableStateReturn {
  currentPage: number;
  sorting: Record<string, 'asc' | 'desc'>;
  paginationState: IPaginationState;
  handlePageChange: (page: number) => void;
  handleSort: (id: string) => void;
  dispatch: (action: { type: string; payload: any }) => void;
  setCurrentPage: (page: number) => void;
  setSorting: (sorting: Record<string, 'asc' | 'desc'>) => void;
}

export function useServerTableState({
  initialFilters,
  initialPage = 1,
  initialSorting = {},
}: UseServerTableStateProps): UseServerTableStateReturn {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sorting, setSorting] = useState<Record<string, 'asc' | 'desc'>>(initialSorting);

  const dispatch = useCallback(
    (action: { type: string; payload: any }) => {
      switch (action.type) {
        case 'page':
          setCurrentPage(action.payload);
          break;
        case 'sorting':
          setSorting(applySorting(action.payload, sorting));
          break;
        default:
          break;
      }
    },
    [sorting]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSort = useCallback(
    (id: string) => {
      return dispatchSorting(dispatch)(id);
    },
    [dispatch]
  );

  const paginationState: IPaginationState = useMemo(
    () => ({
      filters: initialFilters,
      sorting,
      page: currentPage,
    }),
    [initialFilters, sorting, currentPage]
  );

  return {
    currentPage,
    sorting,
    paginationState,
    handlePageChange,
    handleSort,
    dispatch,
    setCurrentPage,
    setSorting,
  };
}
